import { useCallback, useEffect, useRef, useState } from 'react';
import { apiStore } from '@/stores/ApiStore';
import { authFlowStore } from '@/stores/AuthFlowStore';
import { paymentConfirmationStore } from '@/stores/PaymentConfirmationStore';
import { paymentStore } from '@/stores/PaymentStore';
import { subscriptionStore } from '@/stores/SubscriptionStore';
import { clearPendingWebPayment } from '@/utils/pendingWebPayment';
import { readBrowserSessionCode } from '@/utils/sessionEnvironment';

type SessionBootstrapState = 'loading' | 'ready' | 'error';

function removeSessionCodeFromUrl(): void {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.hash.slice(1));
  params.delete('session');
  const remainingHash = params.toString();
  url.hash = remainingHash ? `#${remainingHash}` : '';
  window.history.replaceState(window.history.state, '', url);
}

function isDefinitiveSessionError(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return false;
  }

  const response = error.response;
  if (!response || typeof response !== 'object' || !('status' in response)) {
    return false;
  }

  const status = Number(response.status);
  return status >= 400 && status < 500;
}

export function useSessionBootstrap() {
  const sessionCodeRef = useRef<string | null>(readBrowserSessionCode());
  const hasSessionIntentRef = useRef(sessionCodeRef.current !== null);
  const bootstrapStartedRef = useRef(false);
  const hasSessionIntent = hasSessionIntentRef.current;
  const [state, setState] = useState<SessionBootstrapState>(
    hasSessionIntent || (apiStore.isAuthenticated && !apiStore.user)
      ? 'loading'
      : 'ready',
  );
  const [error, setError] = useState('');

  const bootstrap = useCallback(async () => {
    const sessionCode = sessionCodeRef.current;
    setState('loading');
    setError('');

    try {
      if (sessionCode) {
        await apiStore.consumeSession(sessionCode);
        removeSessionCodeFromUrl();
        sessionCodeRef.current = null;
        authFlowStore.clear();
        subscriptionStore.resetForIdentityChange();
        paymentStore.resetForIdentityChange();
        paymentConfirmationStore.stop();
        clearPendingWebPayment();
      }

      if (hasSessionIntent && !apiStore.isAuthenticated) {
        throw new Error('This session is no longer authenticated.');
      }

      if (apiStore.isAuthenticated) {
        const user = await apiStore.getMe();
        if (!user) {
          throw new Error('Unable to verify this session.');
        }
        if (hasSessionIntent && user.checkoutMode !== 'session') {
          apiStore.logout();
          throw new Error('This session cannot be used for checkout.');
        }
        if (user.checkoutMode === 'session') {
          await subscriptionStore.fetchStatus(true);
        }
      }

      setState('ready');
    }
    catch (bootstrapError) {
      if (sessionCode && isDefinitiveSessionError(bootstrapError)) {
        removeSessionCodeFromUrl();
        sessionCodeRef.current = null;
      }
      setError('This session is invalid or expired. Generate a new session code.');
      setState('error');
    }
  }, [hasSessionIntent]);

  useEffect(() => {
    if (state === 'loading' && !bootstrapStartedRef.current) {
      bootstrapStartedRef.current = true;
      void bootstrap();
    }
  }, [bootstrap, state]);

  const currentEmail = apiStore.user?.email ?? null;
  const requiresSessionStatus = apiStore.user?.checkoutMode === 'session'
    && subscriptionStore.statusOwnerEmail !== currentEmail;

  useEffect(() => {
    if (state === 'ready' && requiresSessionStatus && !subscriptionStore.loading) {
      void subscriptionStore.fetchStatus(true);
    }
  }, [requiresSessionStatus, state]);

  return {
    error,
    hasSessionIntent,
    loading: state === 'loading' || requiresSessionStatus,
    retry: () => {
      void bootstrap();
    },
    state,
  };
}
